export default `<h1 id="rapid-ai-automation-sprint">Rapid AI Automation Sprint</h1>
<p><strong>Turning one high-friction workflow into a working AI-assisted prototype in days, not months.</strong></p>
<p>The Rapid AI Automation Sprint is designed for organisations that already know where time is being lost: manual reporting, repetitive document handling, quality checks, inbox triage, data cleanup, or internal support processes that slow skilled people down.</p>
<p>We keep the scope tight, prove value quickly, and leave you with something practical: a working prototype or automation, a measured business case, and a clear path to production if the return is strong enough.</p>
<hr />
<h2 id="where-it-fits">Where It Fits</h2>
<ul>
<li><strong>One painful workflow:</strong> A process that is repetitive, rules-heavy, or bottlenecked by manual review.</li>
<li><strong>Accessible data:</strong> Spreadsheets, APIs, exports, documents, tickets, emails, or operational logs that can be safely reviewed.</li>
<li><strong>Clear commercial value:</strong> Time saved, faster turnaround, fewer errors, better client response, or better management visibility.</li>
<li><strong>Low-risk experimentation:</strong> A practical prototype with sensible controls before committing to a larger rollout.</li>
</ul>
<hr />
<h2 id="sprint-structure">Sprint Structure</h2>
<table>
<colgroup>
<col style="width: 24%" />
<col style="width: 76%" />
</colgroup>
<thead>
<tr class="header">
<th>Stage</th>
<th>Focus</th>
</tr>
</thead>
<tbody>
<tr class="odd">
<td><strong>Day 1</strong></td>
<td>Workflow mapping, data access check, success metric selection, and risk boundaries.</td>
</tr>
<tr class="even">
<td><strong>Days 2-4</strong></td>
<td>Prototype build, test cases, automation logic, prompt/model selection, and integration sketch.</td>
</tr>
<tr class="odd">
<td><strong>Day 5</strong></td>
<td>Demo, handover, ROI estimate, controls review, and production roadmap.</td>
</tr>
</tbody>
</table>
<hr />
<h2 id="deliverables">Deliverables</h2>
<ul>
<li>A documented workflow map showing the current process, bottlenecks, and automation candidates.</li>
<li>A working prototype, script, dashboard, or AI-assisted operating process for the chosen workflow.</li>
<li>Test examples and acceptance checks so the team can see where the prototype is reliable and where human review remains important.</li>
<li>A short risk and controls note covering privacy, data handling, auditability, and operational failure modes.</li>
<li>A practical next-step plan for turning the prototype into a production-ready internal tool if the return justifies it.</li>
</ul>
<hr />
<h2 id="timeline-investment">Timeline &amp; Investment</h2>
<p><strong>Typical Duration: 5 business days</strong><br />
<strong>Typical Engagement Fee: &pound;2.5k-&pound;7.5k</strong> depending on data access, workflow complexity, and integration depth.</p>
<p>This sprint is deliberately smaller than a full AI consulting engagement. It is a good first paid step when the organisation wants evidence before committing to a broader programme.</p>
<hr />
<h2 id="good-first-use-cases">Good First Use Cases</h2>
<ul>
<li>Automating weekly management reports from spreadsheets, exports, or CRM data.</li>
<li>Summarising client files, tickets, call notes, or case documents with review checkpoints.</li>
<li>Building internal assistants for policy, compliance, engineering, or support knowledge bases.</li>
<li>Creating triage tools for inboxes, leads, incidents, tenders, or operational exceptions.</li>
<li>Replacing brittle manual copy-and-paste workflows with repeatable scripts and safer review steps.</li>
</ul>
`;
